import { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { errMsg, media } from "@/lib/api";
import { SortableList } from "./SortableList";
import { uploadFile } from "./ImageUpload";

export const GalleryEditor = ({ value = [], onChange, testid = "gallery" }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(0);

  const pick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(files.length);
    const urls = [];
    for (const f of files) {
      try {
        urls.push(await uploadFile(f));
      } catch (err) {
        toast.error(`${f.name}: ${errMsg(err)}`);
      }
      setBusy((b) => b - 1);
    }
    onChange([...value, ...urls]);
    if (urls.length) toast.success(`${urls.length} image(s) added — click Save to publish`);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3 mb-3">
        <button type="button" onClick={() => input.current?.click()} disabled={busy > 0} className="inline-flex items-center gap-1.5 text-sm bg-charcoal text-white rounded-md px-3 py-2 hover:bg-black disabled:opacity-60" data-testid={`${testid}-add`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}{busy ? `Uploading ${busy}…` : "Add images"}
        </button>
        <span className="text-xs text-stone-500">{value.length} image(s) · drag or use arrows to reorder</span>
        <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={pick} data-testid={`${testid}-input`} />
      </div>
      {value.length === 0 ? (
        <p className="text-sm text-stone-400 border border-dashed border-stone-300 rounded-md p-4 text-center" data-testid={`${testid}-empty`}>No images yet — the site shows "Image coming soon" placeholders.</p>
      ) : (
        <SortableList
          items={value}
          onChange={onChange}
          getKey={(u, i) => `${u}-${i}`}
          testid={`${testid}-list`}
          className="grid sm:grid-cols-2 gap-2"
          render={(u, i) => (
            <div className="flex items-center gap-3">
              <img src={media(u)} alt="" className="h-14 w-auto max-w-[80px] object-contain bg-stone-50" />
              <span className="text-xs text-stone-500 flex-1">#{i + 1}</span>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-1.5 text-red-600 hover:bg-red-50 rounded" data-testid={`${testid}-remove-${i}`} aria-label="Remove"><Trash2 className="w-4 h-4" /></button>
            </div>
          )}
        />
      )}
    </div>
  );
};
